import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { COUNTRY_CODES } from '../country-codes';
import { CountryCode } from '../first-login.models';

@Component({
  selector: 'app-phone-step',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule,
    MatButtonModule, MatSelectModule, TranslateModule, LoadingSpinnerComponent,
  ],
  host: { class: 'flex-1 flex flex-col min-h-0' },
  template: `
    <form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex flex-col flex-1 min-h-0">
      <div class="flex-1 overflow-auto min-h-0">
        <h2 class="headline-medium text-primary mb-2 hidden md:block" style="font-family: 'DM Sans', sans-serif">{{ 'Add a mobile number' | translate }}</h2>
        <p class="body-large text-on-surface-variant mb-6">{{ "Please enter a valid mobile number. We'll use it for things like password resets, notifications, and important account updates" | translate }}</p>

        <div class="flex gap-3">
          <mat-form-field appearance="outline" class="w-24 shrink-0     
          [&_.mdc-notched-outline__notch]:!max-w-none
          [&_.mdc-floating-label]:!max-w-none
          [&_.mdc-floating-label--float-above]:!max-w-none
          [&_.mdc-floating-label]:!bg-white
          [&_.mdc-floating-label]:!px-1
          [&_.mat-mdc-form-field-required-marker]:!bg-white"
          >
            <mat-label>{{ 'Country' | translate }}</mat-label>

            <mat-select 
              formControlName="countryCode" 
              [panelWidth]="null"
            >
              <mat-select-trigger>
                  <div class="w-full text-center">
                    {{ form.get('countryCode')?.value }}
                  </div>
              </mat-select-trigger>

              @for (country of countries; track country.code) {
                <mat-option [value]="country.dialCode">
                  {{ country.name | translate }} ({{ country.dialCode }})
                </mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="flex-1">
            <mat-label>{{ 'Mobile number' | translate }}</mat-label>
            <input
              matInput
              type="tel"
              formControlName="phoneNumber"
              [placeholder]="'Enter your mobile number' | translate"
              (input)="onPhoneInput($event)"
            />
            @if (form.get('phoneNumber')?.hasError('required') && form.get('phoneNumber')?.touched) {
              <mat-error>{{ 'Mobile number is required' | translate }}</mat-error>
            }
          </mat-form-field>
        </div>
      </div>

      <div class="shrink-0 pt-4">
        <div class="flex items-center justify-between gap-4">
          <button
            mat-stroked-button
            type="button"
            class="w-1/2"
            (click)="remindLater.emit()"
          >
            {{ 'Remind me later' | translate }}
          </button>

          <button
            mat-flat-button
            color="primary"
            type="submit"
            class="w-1/2"
            [disabled]="form.invalid || loading"
          >
            <app-loading-spinner [loading]="loading" [diameter]="20">
              {{ 'Continue' | translate }}
            </app-loading-spinner>
          </button>
        </div>

        <div class="text-center pt-2">
          <button
            mat-button
            type="button"
            class="text-on-surface-variant !text-sm"
            (click)="doNotAskAgain.emit()"
          >
            {{ 'Do not ask me again' | translate }}
          </button>
        </div>
      </div>
    </form>
  `,
})
export class PhoneStepComponent implements OnInit {
  @Input() loading = false;
  @Input() initialCountryCode = '+353';
  @Input() initialPhoneNumber = '';
  @Output() submitPhone = new EventEmitter<{ phoneNumber: string; countryCode: string }>();
  @Output() remindLater = new EventEmitter<void>();
  @Output() doNotAskAgain = new EventEmitter<void>();

  form!: FormGroup;
  countries: CountryCode[] = COUNTRY_CODES;

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      countryCode: [this.initialCountryCode, Validators.required],
      phoneNumber: [this.initialPhoneNumber, Validators.required],
    });
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^\d\s\-()]/g, '');
    this.form.get('phoneNumber')?.setValue(input.value, { emitEvent: false });
  }

  onSubmit(): void {
    if (this.form.invalid) return;
    const { phoneNumber, countryCode } = this.form.value;
    this.submitPhone.emit({ phoneNumber: phoneNumber.replace(/[\s\-()]/g, ''), countryCode });
  }
}
